import React, { useState, useEffect, useRef } from "react";
import Axios from "axios";
import { useParams } from "react-router-dom";
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

// Icons
import { 
  EllipsisVerticalIcon, 
  TrashIcon, 
  PencilSquareIcon,
  ChevronLeftIcon, 
  ChevronRightIcon, 
  MagnifyingGlassIcon,
  DocumentTextIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;

const ViewTemplateTable = (props) => {
 
  // --- STATE ---
  const [open, setOpen] = React.useState(false);
  const [docInfo, setDocInfo] = React.useState(null); 
  const [docName, setDocName] = useState('');
  const [docDescription, setDocDescription] = useState('');
  const [data, setData] = useState(); // Contains user data and language preference
  const [templateIndex, setTemplateIndex] = useState(null); 
  
  // UI State
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [openMenuRowId, setOpenMenuRowId] = useState(null);
  const menuRef = useRef(null);
  const rowsPerPage = 9;
  
  const { id: templateIdParam } = useParams();

  // --- EFFECTS ---

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // NEW useEffect to safely find the template index once props are loaded
  useEffect(() => {
    if (props.clients && templateIdParam) {
      const index = props.clients.findIndex(client => client._id === templateIdParam);
      if (index !== -1) {
        setTemplateIndex(index);
      }
    }
  }, [props.clients, templateIdParam]);

  useEffect(() => {
      Axios ({
         method: "GET",
        withCredentials: true,
          url: `${backEndUrl}/auth/consultantLog`,
        }).then((res) => {
         setData(res.data);       
        });
   }, []) 

  // --- HELPERS ---

  // Helper function to safely extract the localized string for display or editing
  const getLocalizedValue = (rawField) => {
    const userLang = data?.preferredLanguage || 'english'; 
    
    if (!rawField) {
        return '';
    }

    if (typeof rawField === 'string') {
        return rawField;
    }
    
    if (typeof rawField === 'object') {
        if (!rawField.en && !rawField.fr) {
             return ''; 
        }

        if (userLang === 'french') {
            return rawField.fr || rawField.en || ''; 
        } else {
            return rawField.en || '';
        }
    }
    return ''; 
  };

  // --- ACTIONS ---

  const sendForm = async () => {
    const consultantLanguage = data?.preferredLanguage || 'english'; 
   
    try {
      await Axios({
        method: 'PUT',
        withCredentials: true,
        data: {
          name: docName, 
          description: docDescription, 
          template_id: templateIdParam,
          doc_id: docInfo._id,
          consultantLanguage: consultantLanguage, 
        },
        url: `${backEndUrl}/updateClient/updateDocInTemplate`,
      });
      
     window.location.reload();

    } catch (error) {
      console.error(error);
    }
  };

  const handleClickOpen = (docInfo) => {
    setDocName(getLocalizedValue(docInfo.name));
    setDocDescription(getLocalizedValue(docInfo.description));
    setDocInfo(docInfo);
    setOpen(true);
    setOpenMenuRowId(null);
  };

  const handleClose = () => {
    setOpen(false);
    setDocInfo(null);
    setDocName('');
    setDocDescription('');
  };

     const handleDelete = async (id) => {
      try {
        await Axios({
          method: 'DELETE',
          withCredentials: true,
          data: {
             template_id: templateIdParam,
              doc_id: id,
          },
          url: `${backEndUrl}/updateClient/deleteDocInTemplate`,
        });
        
       window.location.reload();
      } catch (error) {
      console.error(error);
    }  
  };  

  // --- RENDER PREPARATION ---

  if (data === undefined || templateIndex === null) {
    return (<>Still loading...</>)
}

  const documents = props.clients[templateIndex]?.documents || [];

  // Filter
  let processedDocs = documents;
  if (searchQuery) {
      processedDocs = processedDocs.filter(doc => {
          const name = getLocalizedValue(doc.name).toLowerCase();
          const desc = getLocalizedValue(doc.description).toLowerCase();
          const query = searchQuery.toLowerCase();
          return name.includes(query) || desc.includes(query);
      });
  }

  // Pagination
  const totalPages = Math.ceil(processedDocs.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const displayDocs = processedDocs.slice(indexOfFirstRow, indexOfLastRow);

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 w-full overflow-hidden mt-6 font-sans backdrop-blur-sm">
      
      {/* HEADER */}
      <div className="px-8 py-6 border-b border-slate-200/60 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gradient-to-r from-slate-50/50 to-white">
        <div>
            <h3 className="font-bold text-2xl text-slate-900 tracking-tight">Documents</h3>
            <p className="text-sm text-slate-500 mt-1">{processedDocs.length} {processedDocs.length === 1 ? 'document' : 'documents'}</p>
        </div>
          <div className="flex items-center gap-4">

            </div>
      </div>
     
      {/* TABLE */}
      <div className="overflow-x-auto min-h-[300px] bg-gradient-to-b from-white to-slate-50/30">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="text-slate-500 text-xs font-semibold uppercase tracking-wider border-b border-slate-200/60 bg-slate-50/80 backdrop-blur-sm">
              <th className="px-8 py-5 pl-8">Name</th>
              <th className="px-6 py-5">Requirements</th>
              <th className="px-6 py-5 text-right pr-8">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {displayDocs.map((doc) => {
              const displayName = getLocalizedValue(doc.name);
              const displayDescription = getLocalizedValue(doc.description);

              return (
                <tr 
                  key={doc._id} 
                  className="group transition-all duration-300 hover:bg-blue-50 hover:shadow-sm"
                >
                  <td className="px-8 py-5 pl-8 border-l-4 border-l-transparent group-hover:border-l-blue-600 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shadow-sm">
                        <DocumentTextIcon className="h-5 w-5" />
                      </div>
                      <span className="font-semibold text-slate-800 text-base">{displayName}</span>
     </div>
                  </td>

                  <td className="px-6 py-5">
                    {displayDescription ? (
                         <HtmlTooltip
                            title={
                            <React.Fragment>
                                <Typography color="inherit" fontSize={16} sx={{ p: 1, fontWeight: 'bold' }}>Description</Typography>
                                <div style={{ maxWidth: 300, padding: '0 8px 8px 8px' }}>
                                <pre style={{fontSize: 14, fontFamily: 'inherit', whiteSpace: 'pre-wrap', margin: 0}}>
                                {displayDescription}
                                </pre>
                            </div>
                            </React.Fragment>
                            }
                        >
                        <div className="flex items-center gap-2 text-slate-500 cursor-help hover:text-blue-600 transition-colors w-fit">
                            <InformationCircleIcon className="h-5 w-5" />
                            <span className="text-sm border-b border-dashed border-slate-400">View Requirements</span>
                        </div>
                      </HtmlTooltip>
                    ) : (
                        <span className="text-slate-400 text-sm italic">No requirements</span>
                    )}
                  </td>

                  <td className="px-6 py-5 pr-8 text-right relative">
                    <div className="relative inline-block text-left">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuRowId(openMenuRowId === doc._id ? null : doc._id);
                          }}
                          className="text-slate-400 hover:text-blue-600 p-2 rounded-lg hover:bg-blue-50 transition-all"
                        >
                          <EllipsisVerticalIcon className="h-6 w-6" />
                        </button>

                        {openMenuRowId === doc._id && (
                          <div ref={menuRef} className="absolute right-0 top-10 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden text-left animate-in fade-in zoom-in-95 duration-200">
                            <button 
                                onClick={() => handleClickOpen(doc)}
                                className="w-full text-left block px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-3 transition-colors"
                            >
                              <PencilSquareIcon className="h-4 w-4" /> Edit Details
                            </button>
                            <div className="h-px bg-slate-100 my-1"></div>
                            <button 
                              onClick={() => handleDelete(doc._id)}
                              className="w-full text-left px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" /> Delete
                            </button>
                          </div>
                        )}
                    </div>
                  </td>
                </tr>
              );
            })}
            
            {displayDocs.length === 0 && (
                <tr>
                    <td colSpan="3" className="px-8 py-20 text-center">
                        <div className="flex flex-col items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                <DocumentTextIcon className="h-8 w-8 text-slate-400" />
                            </div>
                            <p className="text-slate-500 font-medium text-base">No documents found.</p>
                        </div>
                    </td>
                </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="px-8 py-5 border-t border-slate-200/60 flex justify-between items-center bg-gradient-to-r from-slate-50/50 to-white">
            <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-2 text-sm font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:text-blue-600 transition-all px-4 py-2 rounded-lg hover:bg-blue-50 disabled:hover:bg-transparent"
            >
                <ChevronLeftIcon className="h-4 w-4" /> Previous
            </button>
            <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-lg text-sm font-semibold transition-all ${
                            currentPage === page
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                        {page}
                    </button>
                ))}
            </div>
            <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-2 text-sm font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:text-blue-600 transition-all px-4 py-2 rounded-lg hover:bg-blue-50 disabled:hover:bg-transparent"
            >
                Next <ChevronRightIcon className="h-4 w-4" />
            </button>
        </div>
      )}

      {/* EDIT DIALOG */}
<React.Fragment>
 <Dialog
   open={open}
   onClose={handleClose}
   PaperProps={{
     component: 'form',
     onSubmit: (event) => {
       event.preventDefault();
              sendForm(); 
       handleClose();
     },
   }}
 >
  <DialogTitle>Modify document</DialogTitle>
   <DialogContent>
     <DialogContentText>
     Modify document as needed
     </DialogContentText>
     <TextField
      InputLabelProps={{ shrink: true }}
       autoFocus
       required
       margin="dense"
       id="name"
              name="name"
       label="Document name"
              type="text"
      value={docName} 
      onChange={e => setDocName(e.target.value)} 
       fullWidth
       variant="standard"
     />
     <TextField
      InputLabelProps={{ shrink: true }}
       required
       multiline 
       margin="dense"
       id="description"
       name="description"
       label="Document requirement"
       type="text"
       value={docDescription}
       onChange={e => setDocDescription(e.target.value)}
       fullWidth
       variant="standard"
     />
   </DialogContent>
   <DialogActions>
     <Button onClick={handleClose}>Cancel</Button>
            <Button variant='contained' type="submit">Submit</Button> 
   </DialogActions>
 </Dialog>
</React.Fragment>

    </div>
  );
};

export default ViewTemplateTable;